import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@economic/gaudi';
import { refreshCurrentLegacyPage } from '@economic/react-app';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { useLocalization, t } from '../../../../../shared/components/l10n/l10n';
import { BookAndSendReminderDialog, BookAndSendReminderFormData } from './BookAndSendReminderDialog';
import useQuery from '../../../../../shared/hooks/useQuery';
import {
    BookAndSendReminderRequest,
    GetReminderEmailOptionsResponse,
    ReminderBookingResult,
} from '../../../../../../types/internal-api';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { splitEmails } from '../../invoices/utils';
import { BookAndSendReminderDialogRouteStatus } from './types';
import number from '../../../../../shared/util/utilities/number';
import date, { formatISODate } from '../../../../../shared/util/utilities/date';
import { useEmailTemplates } from '../../invoices/utils/useEmailTemplates';
import { InvoiceLayoutType } from '../../../../../../types/internal-api-extra';
import { emailOptionsFromTemplates } from '../../invoices/utils/emailOptionsFromTemplates';
import { emailTemplateFormState } from '../../invoices/utils/emailTemplateFormState';

function BookAndSendReminderDialogContainer() {
    const toast = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const { state = {} } = location;
    const { draftId, customerNumber, singleInvoiceReminder } = state as BookAndSendReminderDialogRouteStatus;

    const [sendingError, setSendingError] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const { loading, data: information, error: informationLoadingError } = useQuery<GetReminderEmailOptionsResponse>(
        `/sales/reminder/get-reminder-email-information?customerId=${customerNumber}`,
        null,
        {
            skip: !draftId || !customerNumber,
        }
    );
    const { data: templates, loading: loadingTemplates } = useEmailTemplates(InvoiceLayoutType.Reminder);

    const loadingTexts = useLocalization(textIds);

    const transformedInformation = useMemo<BookAndSendReminderFormData | undefined>(() => {
        if (!information) {
            return undefined;
        }

        return {
            emailOptions: {
                ...information,
                recipients: information.recipients.join(' '),
                recipientsCc: information.recipientsCc.join(' '),
                recipientsBcc: information.recipientsBcc.join(' '),
                saveSubject: false,
                saveBody: false,
                ...emailOptionsFromTemplates(templates),
            },
            ...emailTemplateFormState(templates),
        };
    }, [information, templates]);

    async function bookAndSend(values: BookAndSendReminderFormData) {
        try {
            setSending(true);

            const request: BookAndSendReminderRequest = {
                draftNumber: draftId,
                customerId: customerNumber,
                sendEmail: true,
                showAfterSending: values.emailOptions.showPdf,
                subject: values.emailOptions.subject,
                saveSubject: values.emailOptions.saveSubject,
                body: values.emailOptions.body,
                saveBody: values.emailOptions.saveBody,
                includePdfLink: values.emailOptions.includePdfLink,
                includePdfAsAttachment: values.emailOptions.includePdfAsAttachment,
                encrypt: values.emailOptions.encrypt,
                recipients: {
                    recipients: splitEmails(values.emailOptions.recipients),
                    recipientsCc: splitEmails(values.emailOptions.recipientsCc),
                    recipientsBcc: splitEmails(values.emailOptions.recipientsBcc),
                },
            };
            if (singleInvoiceReminder) {
                request.singleInvoiceReminder = {
                    ...singleInvoiceReminder,
                    exchangeRate: number.parse(singleInvoiceReminder.exchangeRate)!,
                    reminderFee: number.parse(singleInvoiceReminder.reminderFee)!,
                    interestRate: number.parse(singleInvoiceReminder.interestRate)!,
                    trifleLimit: number.parse(singleInvoiceReminder.trifleLimit)!,
                    totalTrifle: number.parse(singleInvoiceReminder.totalTrifle)!,
                    fromDate: formatISODate(date.parseFromCustomString(singleInvoiceReminder.fromDate)!)!,
                    toDate: formatISODate(date.parseFromCustomString(singleInvoiceReminder.toDate)!)!,
                    interestDate: formatISODate(date.parseFromCustomString(singleInvoiceReminder.interestDate)!)!,
                    paymentDate: formatISODate(date.parseFromCustomString(singleInvoiceReminder.paymentDate)!)!,
                };
            }

            const result = await internalApiClient.post<ReminderBookingResult>(`/sales/reminder/book-and-send-reminder`, request);

            setSending(false);

            if (result.data?.bookedReminders?.length > 0) {
                toast.success(t`TOAST_REMINDER_BOOK_AND_SEND_SUCCESS`);
            } else {
                toast.error(
                    <>
                        {result.data?.validationErrors?.map(error => (
                            <p>{error}</p>
                        ))}
                    </>
                );
            }

            if (values.emailOptions.showPdf && result.data.pdfLink) {
                window.top && window.top.firebirdTask.then(() => window.top?.PopUp2(result.data.pdfLink, 500, 600));
            }

            refreshContentFrameAfterSuccess();
            hide();
        } catch (error) {
            if (error && error.response && error.response.status >= 500) {
                setSendingError(true);
            }
        }
    }

    function refreshContentFrameAfterSuccess() {
        refreshCurrentLegacyPage('content');
    }

    function hide() {
        navigate(location.pathname);
    }

    if (!draftId) {
        return null;
    }

    const disclosure = <RouteDisclosure routeTrigger={'#book-reminder'} />;

    return (
        <BookAndSendReminderDialog
            disclosure={disclosure}
            information={transformedInformation}
            loading={loading || loadingTexts || loadingTemplates || sending}
            onBookAndSend={bookAndSend}
            onHide={hide}
            error={sendingError || !!informationLoadingError}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_REMINDER_HEADER',
    'FORM_BOOKANDSEND_REMINDER_TEXT',
    'FORM_BOOKANDSEND_SHOW_REMINDER',
    'FORM_BOOKANDSEND_REMINDER_BOOKANDSEND',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_RECIPIENTS',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_TOGGLE_CC_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_CC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BCC',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_SUBJECT',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_BODY',
    'FORM_BOOKANDSEND_EMAIL_OPTIONS_REMEMBER',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_OPEN',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_CLOSE',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_ATTACHMENT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_ENCRYPT',
    'FORM_BOOKANDSEND_EMAIL_ADVANCED_OPTIONS_PDF_LINK',
    'FORM_BOOKANDSEND_SELECT_ATTACHMENT_ERROR',
    'FORM_BOOKANDSEND_SELECT_EMAIL_RECIPIENT_ERROR',
    'POPUP_INVOICEETC_ERROR_INVALIDEMAIL',
    'FORM_BOOKANDSEND_ERRORS_HEADER',
    'TOAST_REMINDER_BOOK_AND_SEND_SUCCESS',
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
];

export default BookAndSendReminderDialogContainer;
